import React, { useState } from 'react';
import { LogOut, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import artistIntroLogo from '../../assets/artist_intro.svg';
import WebsitePreviewPanel from './WebsitePreviewPanel';

interface CompletionScreenProps {
  onLogout?: () => void;
  onStatusRefresh?: () => void;
}

const CompletionScreen: React.FC<CompletionScreenProps> = ({ onLogout, onStatusRefresh }) => {
  const [checking, setChecking] = useState(false);

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      if (onStatusRefresh) {
        await onStatusRefresh();
      } else {
        // Fallback: hard reload if no callback provided
        window.location.reload();
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-2xl overflow-hidden">
      {/* <div className="flex flex-col lg:grid lg:grid-cols-5 flex-1 min-h-0 overflow-hidden"></div> */}
      <div className="flex flex-col lg:grid lg:grid-cols-5 flex-1 min-h-0 overflow-hidden">
        {/* ─── Left Column: Content ───────────────── */}
        <div className="lg:col-span-2 flex flex-col flex-1 min-h-0 overflow-y-auto">
          <div className="px-6 sm:px-10 lg:px-16 pt-8 lg:pt-10 flex-1">
            <div className="max-w-2xl mx-auto w-full">
              {/* Logo */}
              <img src={artistIntroLogo} alt="Artist Intro" className="h-10 mb-12 shrink-0" />

              {/* Heading */}
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight">
                Your <span className="text-primary">Artist Intro</span> has been submitted
                {' '}and is under review!
              </h1>

              {/* Subtitle */}
              <p className="text-gray-500 text-sm sm:text-base mb-1 leading-relaxed">
                We'll notify you by email once it's approved and ready to publish.
              </p>

              <p className="text-gray-400 text-sm mb-8">
                This usually takes less than 24 hours.
              </p>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleCheckStatus}
                  disabled={checking}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
                  {checking ? 'Checking…' : 'Check Status'}
                </Button>
              </div>

            </div>
          </div>

          {/* Sign Out — pinned to bottom */}
          {onLogout && (
            <div className="shrink-0 px-6 sm:px-10 lg:px-16 pb-8">
              <div className="max-w-2xl mx-auto w-full">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={onLogout}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 min-h-100 border-t border-border lg:border-l lg:border-t-0">
          <WebsitePreviewPanel statusText="Submitted website preview" />
        </div>
      </div>
    </div>
  );
};

export default CompletionScreen;